import type { AxiosInstance, AxiosResponse } from 'axios';
import type { ServiceResult } from '../types/common/ServiceResult';
import { apiClient } from '../client/client';

export interface AdminReelViewModel {
  id: string;
  videoUrl: string;
  userId: string;
  createdAtUtc?: string | null;
  listingId?: string | null;
  listingTitle?: string | null;
  listingCode?: string | null;
  statusId: number;
  rejectionReason?: string | null;
}

function normalizeAdminReel(raw: unknown): AdminReelViewModel | null {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!o) return null;
  const id = String(o.id ?? o.Id ?? '');
  const videoUrl = String(o.videoUrl ?? o.VideoUrl ?? '');
  if (!id || !videoUrl) return null;
  return {
    id,
    videoUrl,
    userId: String(o.userId ?? o.UserId ?? ''),
    createdAtUtc: (() => {
      const v = o.createdAtUtc ?? o.CreatedAtUtc;
      return typeof v === 'string' ? v : null;
    })(),
    listingId: (() => {
      const v = o.listingId ?? o.ListingId;
      return typeof v === 'string' && v.length > 0 ? v : null;
    })(),
    listingTitle: (() => {
      const v = o.listingTitle ?? o.ListingTitle;
      return typeof v === 'string' ? v : null;
    })(),
    listingCode: (() => {
      const v = o.listingCode ?? o.ListingCode;
      return typeof v === 'string' ? v : null;
    })(),
    statusId: Number(o.statusId ?? o.StatusId ?? 4),
    rejectionReason: (() => {
      const v = o.rejectionReason ?? o.RejectionReason;
      return typeof v === 'string' ? v : null;
    })(),
  };
}

function extractList(raw: unknown): AdminReelViewModel[] {
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;
  const candidates = [obj.data, obj.Data, obj];
  for (const c of candidates) {
    if (Array.isArray(c)) {
      return c.map(normalizeAdminReel).filter((x): x is AdminReelViewModel => x != null);
    }
  }
  return [];
}

function toServiceResult(response: AxiosResponse): ServiceResult {
  const data = response.data;
  if (data && typeof data === 'object' && 'isSuccess' in data) return data;
  return { isSuccess: response.status >= 200 && response.status < 300, statusCode: response.status };
}

export class ReelApprovalService {
  private static readonly BASE = '/api/reel-approval';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getPendingStandalone(): Promise<AdminReelViewModel[]> {
    const response = await this.client.get(`${ReelApprovalService.BASE}/pending-standalone`);
    return extractList(response.data);
  }

  async getPendingByListing(listingId: string): Promise<AdminReelViewModel[]> {
    const response = await this.client.get(`${ReelApprovalService.BASE}/pending-by-listing/${listingId}`);
    return extractList(response.data);
  }

  async approveOrReject(
    statusId: number,
    reelId: string,
    body?: { rejectionReason?: string | null }
  ): Promise<ServiceResult> {
    const url = `${ReelApprovalService.BASE}/approve-or-reject/${statusId}/${reelId}`;
    const response =
      body !== undefined
        ? await this.client.put(url, body, { headers: { 'Content-Type': 'application/json' } })
        : await this.client.put(url);
    return toServiceResult(response);
  }
}

export const reelApprovalService = new ReelApprovalService();
